import React from "react";
import { makeStyles } from "tss-react/mui";

import ArrowFileUp from "@dashboard/files/components/ArrowFileUp";
import { Typography } from "@mui/material";

const useStyles = makeStyles()((theme) => ({
    label: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: `2px dashed ${theme.palette.emteria.border.primary}`,
        backgroundColor: theme.palette.emteria.background.paper,
        borderRadius: 12,
        padding: theme.spacing(5),
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        flex: 1,
        cursor: "pointer",
    },
    labelDragging: {
        borderColor: theme.palette.emteria.action,
        backgroundColor: theme.palette.emteria.background.paperDeep,
    },
    labelError: {
        borderColor: theme.palette.emteria.error,
    },
    labelDisabled: {
        opacity: 0.5,
        cursor: "default",
    },
    container: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    title: {
        fontSize: 18,
        textAlign: "center",
        color: theme.palette.emteria.text.primary,
    },
    caption: {
        fontSize: 14,
        textAlign: "center",
        color: theme.palette.emteria.text.primary,
    },
    selectedFileName: {
        fontSize: 16,
        wordBreak: "break-all",
        textAlign: "center",
        color: theme.palette.emteria.text.primary,
    },
}));

type Props = {
    inputId: string;
    label: React.ReactNode;
    onFilesSelected: (files: File[]) => void;
    accept?: string;
    caption?: string;
    className?: string;
    disabled?: boolean;
    error?: boolean;
    multiple?: boolean;
    selectedFileName?: string;
};

export const FileDropzone = ({
    inputId,
    label,
    onFilesSelected,
    accept,
    caption,
    className,
    disabled = false,
    error = false,
    multiple = false,
    selectedFileName,
}: Props) => {
    const { classes, cx } = useStyles();
    const [isDragging, setIsDragging] = React.useState(false);

    const handleFiles = (files: File[]) => {
        if (disabled || !files.length) {
            return;
        }

        onFilesSelected(multiple ? files : files.slice(0, 1));
    };

    const onDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        if (!disabled) {
            setIsDragging(true);
        }
    };

    const onDragLeave = () => {
        setIsDragging(false);
    };

    const onDrop = (event: React.DragEvent<HTMLLabelElement>) => {
        event.preventDefault();
        setIsDragging(false);
        handleFiles(Array.from(event.dataTransfer.files));
    };

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        handleFiles(files);
        event.target.value = "";
    };

    return (
        <label
            htmlFor={inputId}
            className={cx(
                classes.label,
                isDragging ? classes.labelDragging : null,
                error ? classes.labelError : null,
                disabled ? classes.labelDisabled : null,
                className,
            )}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            <div className={classes.container}>
                <ArrowFileUp color={error ? "red" : undefined} />
                {selectedFileName ? (
                    <Typography className={classes.selectedFileName}>{selectedFileName}</Typography>
                ) : (
                    <Typography className={classes.title}>{label}</Typography>
                )}
                {caption ? <Typography className={classes.caption}>{caption}</Typography> : null}
                <input
                    id={inputId}
                    type="file"
                    multiple={multiple}
                    accept={accept}
                    onChange={onChange}
                    hidden
                    disabled={disabled}
                />
            </div>
        </label>
    );
};
